import { useEffect, useRef } from 'react';
import { usePositionPersistenceAutoLayout } from './positionPersistence/usePositionPersistenceAutoLayout';

/**
 * Saves auto-layout positions for tasks without saved coords; wraps Tidy with persistence
 * and multi-campaign / partial-selection behavior. See positionPersistence/ for helpers.
 */
export function usePositionPersistence({
  tasks,
  autoPositions,
  campaignId,
  onPositionChange,
  onPositionChangeBatch,
  interaction,
  triggerLayout,
  triggerLayoutInstant,
  interactionAutoLayout,
  isMultiCampaignMode = false,
  nodeDimensions = {},
  onAnimationStart,
  onAnimationEnd,
  elkPositionsOkRef,
}) {
  const savedPositionsRef = useRef(new Set());
  const fullTidyPersistGenerationRef = useRef(0);
  const autoPositionsRef = useRef(autoPositions);
  autoPositionsRef.current = autoPositions;

  useEffect(() => {
    if (!onPositionChange || tasks.length === 0) return;

    const tasksNeedingPositions = tasks.filter((task) => {
      if (savedPositionsRef.current.has(task.id)) return false;
      const hasValidSavedPosition = Number.isFinite(task.x) && Number.isFinite(task.y);
      if (hasValidSavedPosition) return false;
      if (!autoPositions[task.id]) return false;
      return true;
    });

    if (tasksNeedingPositions.length === 0) return;

    tasksNeedingPositions.forEach((task) => {
      const pos = autoPositions[task.id];
      if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
        savedPositionsRef.current.add(task.id);
        onPositionChange(task.id, pos);
      } else if (pos) {
        console.warn('[usePositionPersistence] Skipping save of invalid position for task:', task.id, pos);
      }
    });
  }, [tasks, autoPositions, onPositionChange]);

  useEffect(() => {
    savedPositionsRef.current = new Set();
  }, [campaignId]);

  const { handleAutoLayout, handleAutoLayoutInstant } = usePositionPersistenceAutoLayout({
    tasks,
    autoPositionsRef,
    onPositionChange,
    onPositionChangeBatch,
    interaction,
    triggerLayout,
    triggerLayoutInstant,
    interactionAutoLayout,
    isMultiCampaignMode,
    nodeDimensions,
    onAnimationStart,
    onAnimationEnd,
    elkPositionsOkRef,
    fullTidyPersistGenerationRef,
    campaignId,
  });

  return {
    savedPositionsRef,
    handleAutoLayout,
    handleAutoLayoutInstant,
  };
}

export default usePositionPersistence;
